import { Link } from 'react-router-dom';
import './TermsPage.scss';

export function TermsPage() {
  return (
    <main className="terms-page">
      <div className="terms-page__container">
        <header className="terms-page__header">
          <h1 className="terms-page__title">Terms of Service</h1>
          <p className="terms-page__subtitle">
            Last updated: March 3, 2026
          </p>
        </header>

        <div className="terms-page__intro">
          <p className="terms-page__text">
            These Terms of Service ("Terms") govern your use of the ShareQ browser
            extension and related services ("Service") operated by ShareQ ("Company",
            "we", "us", or "our").
          </p>
          <p className="terms-page__text">
            By accessing or using the Service, you agree to be bound by these Terms.
          </p>
        </div>

        <section className="terms-page__section">
          <h2 className="terms-page__section-title">1. Description of Service</h2>
          <div className="terms-page__card">
            <p className="terms-page__text">
              The Service allows users to securely share HTTP request data through
              generated shareable links.
            </p>
            <p className="terms-page__text">
              The Service may offer free and paid subscription plans with additional features.
            </p>
          </div>
        </section>

        <section className="terms-page__section">
          <h2 className="terms-page__section-title">2. Eligibility</h2>
          <div className="terms-page__card">
            <p className="terms-page__text">
              You must be at least 18 years old to use the Service.
            </p>
            <p className="terms-page__text">
              By using the Service, you represent that you have the legal authority
              to enter into these Terms.
            </p>
          </div>
        </section>

        <section className="terms-page__section">
          <h2 className="terms-page__section-title">3. Account Registration</h2>
          <div className="terms-page__card">
            <p className="terms-page__text">Users authenticate via Google OAuth.</p>
            <p className="terms-page__text">You are responsible for:</p>
            <ul className="terms-page__list">
              <li>Maintaining the security of your account</li>
              <li>All activities under your account</li>
              <li>Providing accurate information</li>
            </ul>
            <p className="terms-page__text terms-page__text--muted">
              We are not responsible for unauthorized access resulting from your
              failure to secure your account.
            </p>
          </div>
        </section>

        <section className="terms-page__section">
          <h2 className="terms-page__section-title">4. Subscriptions &amp; Payments</h2>
          <div className="terms-page__card">
            <p className="terms-page__text">
              Paid subscriptions are processed by Paddle, our authorized Merchant of Record.
            </p>
            <p className="terms-page__text">By purchasing a subscription:</p>
            <ul className="terms-page__list">
              <li>You agree to Paddle's payment terms and policies</li>
              <li>You authorize Paddle to charge your payment method</li>
              <li>Taxes may be applied where required</li>
            </ul>
            <p className="terms-page__text terms-page__text--muted">
              Subscription fees are billed in advance and are non-refundable except
              where required by law.
            </p>
          </div>
        </section>

        <section className="terms-page__section">
          <h2 className="terms-page__section-title">5. Cancellation &amp; Refunds</h2>
          <div className="terms-page__card">
            <ul className="terms-page__list">
              <li>You may cancel your subscription at any time through your Paddle customer portal.</li>
              <li>Access to paid features will continue until the end of the billing period.</li>
              <li>
                Refund requests are handled in accordance with Paddle's refund policy
                and applicable consumer protection laws.
              </li>
            </ul>
          </div>
        </section>

        <section className="terms-page__section">
          <h2 className="terms-page__section-title">6. Acceptable Use</h2>
          <div className="terms-page__card">
            <p className="terms-page__text">You agree not to:</p>
            <ul className="terms-page__list terms-page__list--crossed">
              <li>Share illegal, harmful, or infringing content</li>
              <li>Upload malicious code</li>
              <li>Attempt to reverse engineer or disrupt the Service</li>
              <li>Use the Service to distribute sensitive personal data without authorization</li>
            </ul>
            <p className="terms-page__text terms-page__text--muted">
              We reserve the right to suspend or terminate accounts violating these rules.
            </p>
          </div>
        </section>

        <section className="terms-page__section">
          <h2 className="terms-page__section-title">7. Data &amp; Privacy</h2>
          <div className="terms-page__card">
            <p className="terms-page__text">
              Your use of the Service is also governed by our{' '}
              <Link to="/privacy" className="terms-page__link">Privacy Policy</Link>.
            </p>
            <p className="terms-page__text">
              We process data solely to provide the Service and do not sell personal data.
            </p>
            <p className="terms-page__text">
              Users are responsible for ensuring they have the right to share any
              request data uploaded to the Service.
            </p>
          </div>
        </section>

        <section className="terms-page__section">
          <h2 className="terms-page__section-title">8. Intellectual Property</h2>
          <div className="terms-page__card">
            <p className="terms-page__text">
              All rights, title, and interest in the Service remain the property
              of the Company.
            </p>
            <p className="terms-page__text">
              You may not copy, modify, distribute, or create derivative works
              without permission.
            </p>
          </div>
        </section>

        <section className="terms-page__section">
          <h2 className="terms-page__section-title">9. Service Availability</h2>
          <div className="terms-page__card">
            <p className="terms-page__text">
              We may modify, suspend, or discontinue the Service at any time.
            </p>
            <p className="terms-page__text">
              We do not guarantee uninterrupted or error-free operation.
            </p>
          </div>
        </section>

        <section className="terms-page__section">
          <h2 className="terms-page__section-title">10. Disclaimer of Warranties</h2>
          <div className="terms-page__card">
            <p className="terms-page__text">
              The Service is provided "as is" and "as available."
            </p>
            <p className="terms-page__text">
              We disclaim all warranties, express or implied, including fitness
              for a particular purpose.
            </p>
          </div>
        </section>

        <section className="terms-page__section">
          <h2 className="terms-page__section-title">11. Limitation of Liability</h2>
          <div className="terms-page__card">
            <p className="terms-page__text">
              To the maximum extent permitted by law:
            </p>
            <ul className="terms-page__list">
              <li>
                The Company shall not be liable for indirect, incidental, special,
                or consequential damages.
              </li>
              <li>
                Our total liability shall not exceed the amount paid by you in the
                past 12 months.
              </li>
            </ul>
          </div>
        </section>

        <section className="terms-page__section">
          <h2 className="terms-page__section-title">12. Termination</h2>
          <div className="terms-page__card">
            <p className="terms-page__text">
              We may suspend or terminate your account if:
            </p>
            <ul className="terms-page__list">
              <li>You violate these Terms</li>
              <li>Required by law</li>
              <li>Necessary to protect the Service or other users</li>
            </ul>
          </div>
        </section>

        <section className="terms-page__section">
          <h2 className="terms-page__section-title">13. Governing Law</h2>
          <div className="terms-page__card">
            <p className="terms-page__text">
              These Terms are governed by applicable law, without regard to
              conflict of law principles.
            </p>
          </div>
        </section>

        <section className="terms-page__section">
          <h2 className="terms-page__section-title">14. Changes to Terms</h2>
          <div className="terms-page__card">
            <p className="terms-page__text">
              We may update these Terms from time to time.
            </p>
            <p className="terms-page__text">
              Continued use of the Service after updates constitutes acceptance
              of the revised Terms.
            </p>
          </div>
        </section>

        <section className="terms-page__section">
          <h2 className="terms-page__section-title">15. Contact</h2>
          <div className="terms-page__card terms-page__card--contact">
            <p className="terms-page__text">
              If you have questions about these Terms, contact:
            </p>
            <div className="terms-page__contact-item">
              <span className="terms-page__contact-label">Email</span>
              <a href="mailto:support@shareq.site" className="terms-page__contact-link">
                support@shareq.site
              </a>
            </div>
          </div>
        </section>

      </div>
    </main>
  );
}
